/**
 * A form-aware confirmation button. Shows an alert dialog before firing its action.
 *
 * Reads `canSubmit` and `isSubmitting` from form context to automatically
 * disable the trigger — same pattern as `SubmitButton`.
 */

import type { WithOptional } from '@snootsvet/shared'

import { useFormContext } from '@/frontend/components/form/form-context'
import { ConfirmButton, type ConfirmButtonProps } from '@/frontend/components/ui/confirm-button'

type ConfirmSubmitButtonProps = ConfirmButtonProps

export function ConfirmSubmitButton({
  children,
  disabled,
  onConfirm,
  ...props
}: WithOptional<ConfirmSubmitButtonProps, 'onConfirm'>) {
  const form = useFormContext()
  const handleConfirm = () => (onConfirm ? onConfirm() : form.handleSubmit())

  const handlePreValidate = async () => {
    const errors = await form.validate('submit')
    return Object.keys(errors).length === 0
  }

  return (
    <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting] as const}>
      {([canSubmit, isSubmitting]) => (
        <ConfirmButton
          disabled={disabled || !canSubmit || isSubmitting}
          onConfirm={handleConfirm}
          preValidate={handlePreValidate}
          {...props}
        >
          {children}
        </ConfirmButton>
      )}
    </form.Subscribe>
  )
}
